import { IReviewer } from '@publishing-engine/domain/models';
import { IInviteReviewer } from '../components/Submission/ReviewTab/InviteReviewer/commonTypes';
import { IBlockedReviewer } from '@publishing-engine/domain/views';

export const handleEmptyReviewer = (reviewers: IInviteReviewer[]) => {
  return reviewers.filter(reviewer => !reviewer.email);
};

export const findDuplicateIndices = (
  reviewers: IInviteReviewer[],
  existingReviewers: IReviewer[] | Pick<IReviewer, 'email'>[]
) => {
  const emailSet = new Set(existingReviewers.map(reviewer => reviewer.email));
  const duplicateIndices: number[] = [];

  reviewers.forEach((reviewer, index) => {
    if (reviewer.email !== '' && emailSet.has(reviewer.email)) {
      duplicateIndices.push(index);
    } else {
      emailSet.add(reviewer.email);
    }
  });

  return duplicateIndices;
};

export const categorizeDuplicateReviewers = (
  reviewers: IInviteReviewer[],
  existingReviewers: IReviewer[] | Pick<IReviewer, 'email'>[]
): Record<string, { isInputDuplicate: boolean; isAlreadyInvited: boolean }> => {
  const existingEmailSet = new Set(
    existingReviewers.map(reviewer => reviewer.email)
  );
  const inputEmailCount = new Map<string, number>();

  // Count occurrences of each email in the input
  reviewers.forEach(reviewer => {
    const email = reviewer.email?.trim();
    if (email) {
      inputEmailCount.set(email, (inputEmailCount.get(email) || 0) + 1);
    }
  });

  // Categorize each unique email
  const result: Record<
    string,
    { isInputDuplicate: boolean; isAlreadyInvited: boolean }
  > = {};

  inputEmailCount.forEach((count, email) => {
    const isInputDuplicate = count > 1;
    const isAlreadyInvited = existingEmailSet.has(email);

    if (isInputDuplicate || isAlreadyInvited) {
      result[email] = {
        isInputDuplicate,
        isAlreadyInvited
      };
    }
  });

  return result;
};

export const findBlockedReviewers = (
  reviewers: IInviteReviewer[],
  blockedReviewers: IBlockedReviewer[]
) => {
  const blockedReasonMap = new Map<string, string>();
  blockedReviewers.forEach(reviewer => {
    blockedReasonMap.set(reviewer.email, reviewer.blockReason);
  });

  const duplicateIndices: { index: number; blockReason: string }[] = [];
  reviewers.forEach((reviewer, index) => {
    const blockReason = blockedReasonMap.get(reviewer.email);
    if (blockReason) {
      duplicateIndices.push({ index, blockReason: blockReason });
    } else {
      blockedReasonMap.set(reviewer.email, '');
    }
  });

  return duplicateIndices;
};

export const getBlockedReviewerErrorMessage = (
  blockReason: string,
  email?: string
): string => {
  const defaultMessage = `${
    email ? email : 'This Reviewer'
  } is unable to participate and can no longer be invited for this submission.`; // this is fallback message

  const errorMessageForReason: Record<string, string> = {
    declined: `This reviewer has declined the invitation. You can reinvite them from the reviews tab.`,
    removed: ` This Reviewer previously reviewed this submission, but was removed by editors. You can reinvite them from the reviews tab.`
  };

  return blockReason && errorMessageForReason[blockReason]
    ? errorMessageForReason[blockReason]
    : defaultMessage;
};
